/**
 * Shared pricing types — single source of truth for SPA, Worker, and Shopify sync.
 * Mirrors pricing/pricing.json structure (the canonical seed; D1 is live source after import).
 */

/** Concrete paper identifiers (keys of pricing.papers). */
export type PaperId =
  | 'gloss'
  | 'lustre'
  | 'bauhaus-matt'
  | 'bauhaus-lustre'
  | 'bauhaus-metallic'
  | 'ilford-pearl'
  | 'ilford-cottonrag-smooth'
  | 'ilford-cottonrag-textured'
  | 'bizan-medium'
  | 'bizan-thick';

/** Paper PRICE tier: selects which price applies. */
export type PaperTier = 'ctype' | 'standard' | 'fineArt' | 'specialty';

/** Paper UI grouping in the picker (may differ from price tier — e.g. Metallic groups with Bauhaus but prices as fineArt). */
export type PaperUiGroup = 'ctype' | 'standard' | 'fineArt' | 'specialty';

export type ProductLineId = 'ctype_standard' | 'ctype_enlargements' | 'fine_art';

/** Broad product family used on orders/manifests. */
export type ProductFamily = 'ctype' | 'fine_art' | 'frame';

/** Frame config tier — set by frame-size stock, shared across papers of the same price tier. */
export type FrameConfigTier = 'ctype' | 'standard' | 'premium';

/** How a print sits in its frame. Anything that is not printToEdge is matted,
 *  which is what decides the staff-facing tape margin — use isMattedBorder()
 *  rather than comparing to a specific style, so adding a style cannot silently
 *  bake a matted print borderless. */
export type BorderStyle = 'printToEdge' | 'doubleMatt' | 'singleMatt';

/** Border rendered at bake. `white` = white margin within the print; `handling` = fine-art extra paper around the print; `none` = print to edge. */
export type BorderMode = 'none' | 'white' | 'handling';

/** Admin-editable border defaults (do NOT change price). */
export interface BorderConfig {
  ctype: {
    /** default white-border width (mm) */
    defaultMm: number;
    /** wider white-border width (mm) for larger prints */
    largeMm: number;
    /** short-edge inches at/above which largeMm applies (e.g. 8" prints) */
    largeThresholdInches: number;
  };
  fineArt: {
    /** white-border width as % of the print's short edge */
    whitePercentShortEdge: number;
    /** minimum white-border width (mm) */
    whiteMinMm: number;
    /** fine-art handling border (mm of extra paper around the print) */
    handlingMm: number;
  };
  /** Handling border (mm of extra paper) baked around framed DOUBLE-MATT prints
   * (tape margin behind the matte); printToEdge frames bake borderless. Staff-facing
   * only — customer previews show framed prints borderless. Optional — older
   * published pricing versions lack it; consumers fall back to 10. */
  framedMattHandlingMm?: number;
}

export interface PaperInfo {
  label: string;
  priceTier: PaperTier;
  uiGroup: PaperUiGroup;
  gsm: number | null;
  description: string;
  badge?: string;
}

export interface CTypeSize {
  size: string; // e.g. "4x6", "5x7.5"
  inches: [number, number];
  price: number;
  bulkPrice: number;
  pano?: boolean;
  /**
   * Optional per-size paper restriction (pricing v9): the ONLY finishes this
   * size prints on (must be a subset of the line's papers). Absent = every
   * finish of the line. E.g. 8x12 carries ["lustre"] — Lustre only.
   */
  papers?: PaperId[];
}

export interface FineArtSize {
  size: string; // e.g. "16x20", "A3"
  inches: [number, number];
  standard: number;
  fineArt: number;
}

/** Specialty fine-art entry with an explicit (paper, size) price (e.g. Awagami Bizan A1). */
export interface SpecialtyEntry {
  paper: PaperId;
  size: string;
  inches: [number, number];
  price: number;
}

/**
 * Frame colour. An AXIS over the configs, not a copy of them: the same supplier,
 * sizes and prices in every colour, so adding one costs a `colours` entry and
 * nothing else (owner 2026-07-28, white now, oak later).
 *
 * `black` is the DEFAULT and its skuCode is deliberately EMPTY, so every id and
 * SKU minted before colour existed stays byte-identical and nothing migrates.
 */
export type FrameColourId = string;

export interface FrameColour {
  /** Customer-facing word, e.g. "White". */
  label: string;
  /** SKU/id suffix. Empty string = the unsuffixed default (black). */
  skuCode: string;
}

/** One stocked frame configuration: a print size that mounts in a specific frame size + border. */
export interface FrameConfig {
  printSize: string;
  frameSize: string;
  border: BorderStyle;
  price: number;
}

/**
 * Urgent-processing upsell (percentage surcharge on the prints+frames subtotal).
 * Two modes derived per order (urgencyMode): C-Type-only orders pay the fee at
 * checkout ('paid'); anything with fine art / custom sizes / frames may only
 * REQUEST urgency ('request') — staff confirm turnaround and invoice the fee
 * separately. Optional — older published pricing versions lack it; the upsell
 * stays hidden until the block exists with enabled true.
 */
export interface UrgencyConfig {
  enabled: boolean;
  /** Surcharge as a percentage of the subtotal (100 = double the order). */
  surchargePct: number;
  /** Sydney-time cutoff "HH:MM" — Mode A orders paid before this are due same day. */
  sameDayCutoff: string;
  /** Days the lab prints, as mon..sun tokens (e.g. ["mon","tue","wed","thu","fri"]).
   * Urgency promises/due dates roll to the next production day. Optional —
   * older published pricing versions lack it; consumers fall back to Mon-Fri. */
  productionDays?: string[];
  /** Display name, e.g. "Urgent processing". */
  label: string;
  /** Dialog copy for Mode A (paid upfront, same-day turnaround). */
  copySameDay: string;
  /** Dialog copy for Mode B (request only, fee invoiced after staff confirm). */
  copyRequest: string;
}

/**
 * A resolved framed product (print + frame, one variant).
 * frameId = fr-{paperCode}-{printSize} for black, plus `-{skuCode}` for any
 * other colour (e.g. fr-BM-8x10 / fr-BM-8x10-wh).
 */
export interface FramedProduct {
  frameId: string;
  sku: string;
  paper: PaperId;
  family: ProductFamily; // always 'frame' family for cart grouping, but carries the print paper
  printSize: string;
  frameSize: string;
  border: BorderStyle;
  price: number;
  /** inches of the PRINT (what the bake agent produces). */
  printInches: [number, number];
  /** Physical inches of the FRAME opening. Resolved from the catalogue, not
   *  parsed from the name — "A2".split('x') is [NaN], which rendered the room
   *  view as a dot and the 3D view as a white 1:1 square (owner 2026-08-28). */
  frameInches: [number, number];
  /** Frame colour key, e.g. 'black' | 'white'. */
  colour: FrameColourId;
  /** Customer-facing colour word, e.g. "White". */
  colourLabel: string;
}

/**
 * Identity of a frame CONFIGURATION, paper aside — the SINGLE definition of
 * "the same frame" used for upsell grouping, paper-sibling lookup and dropdown
 * highlighting.
 *
 * These four things used to key on `printSize|frameSize|border` in four
 * separate places. Adding colour to three of them and missing the fourth fails
 * SILENTLY (a colour vanishes from a list, or picking a paper flips the
 * colour), so the key lives here once and every site calls it.
 */
export function frameConfigKey(fp: {
  printSize: string;
  frameSize: string;
  border: BorderStyle;
  colour: FrameColourId;
}): string {
  return `${fp.printSize}|${fp.frameSize}|${fp.border}|${fp.colour}`;
}

export interface TurnaroundRange {
  minDays: number;
  maxDays: number;
}

export interface PricingData {
  version: number;
  currency: string;
  gstIncluded: boolean;
  papers: Record<string, PaperInfo>;
  productLines: {
    ctype_standard: {
      label: string;
      process: string;
      papers: PaperId[];
      bulkThreshold: number;
      bulkNote: string;
      sizes: CTypeSize[];
    };
    ctype_enlargements: {
      label: string;
      process: string;
      papers: PaperId[];
      bulkThreshold: number;
      bulkNote: string;
      sizes: CTypeSize[];
    };
    fine_art: {
      label: string;
      process: string;
      papers: PaperId[];
      sizes: FineArtSize[];
      specialty: SpecialtyEntry[];
      customSizing: string;
    };
    framing: {
      label: string;
      description: string;
      borderStyles: Record<BorderStyle, string>;
      tierForPaper: Record<string, FrameConfigTier>;
      configs: Record<FrameConfigTier, FrameConfig[]>;
      paperCodes: Record<string, string>;
      /**
       * Frame colours. OPTIONAL so published pricing versions from before
       * 2026-07-28 still validate and resolve: absent means black only, which
       * is exactly what those versions meant.
       */
      colours?: Record<FrameColourId, FrameColour>;
    };
  };
  borders: BorderConfig;
  fulfilment: Record<string, { label: string; address?: string; price?: number; note?: string }>;
  limits: {
    maxPhotosPerOrder: number;
    /** Total original bytes per order (customer-facing cap, e.g. 5 GB). */
    maxOrderBytes: number;
    maxFileBytes: number;
    maxDecodeMegapixels: number;
    /** Computers only (owner 2026-10-20). Optional: older published pricing
     * lacks it, so consumers fall back to the seed, then maxDecodeMegapixels. */
    maxDecodeMegapixelsDesktop?: number;
    maxCartGroups: number;
  };
  /** Cart "Add Instructions" dialog: quick-add chips + char cap. Optional —
   * older published pricing versions lack it; consumers fall back to the seed. */
  orderNotes?: { quickAdd: string[]; maxChars: number };
  /** Free-shipping offer: orders of SMALL-FORMAT (loose C-Type) prints only —
   * any frame or fine-art/custom print disqualifies. Cart shows the progress
   * nudge; the actual rate lives in Shopify shipping settings. Optional —
   * older published pricing versions lack it; consumers fall back to the seed. */
  freeShipping?: { threshold: number };
  /** Urgent-processing upsell. Optional — absent or enabled:false hides it. */
  urgency?: UrgencyConfig;
  /** Custom fine-art sizing: price = ceil(L_in × W_in × perSqIn[group]) whole dollars. */
  customSizing: {
    enabled: boolean;
    perSqIn: { standard: number; fineArt: number };
  /** Price floor per group — a custom print never undercuts the smallest
   *  listed size of its tier (owner 2026-08-24). Optional for older blobs. */
  minPrice?: { standard: number; fineArt: number };
    roundUpToDollar: boolean;
    minInches: number;
    maxInches: [number, number];
  };
  printCheck: {
    /** DPI at/above which a print is "best". */
    targetDpi: number;
    /** DPI at/above which a print is "good". */
    goodDpi: number;
    /** Minimum acceptable DPI per product family — below this the size is blocked (override allowed). */
    minDpi: { ctype: number; fineArt: number };
    ratioTolerance: number;
  };
  /**
   * Pre-checkout turnaround dialog (owner 2026-07-31). Whole-order estimate is
   * the SLOWEST family present in the cart. Optional: older published pricing
   * lacks it and the dialog simply never shows.
   */
  turnaroundDays?: {
    ctype: TurnaroundRange;
    fineArt: TurnaroundRange;
    framed: TurnaroundRange;
  };
  turnaround: string;
  hours: string;
}

/** A flattened, resolved size entry used throughout the portal (bare prints only). */
export interface ResolvedSize {
  /** Canonical size id, e.g. "ct-4x6", "fa-16x20", "fa-A3". */
  sizeId: string;
  family: 'ctype' | 'fine_art';
  productLine: ProductLineId;
  /** Raw size string from pricing data, e.g. "4x6". */
  size: string;
  /** Display label, e.g. `4 × 6"` or `A3 (11.7 × 16.5")`. */
  label: string;
  inches: [number, number];
  pano: boolean;
  /** Base price. For fine art this is per-tier; use priceFor() to resolve by paper. */
  price?: number;
  bulkPrice?: number;
  bulkThreshold?: number;
  standard?: number;
  fineArt?: number;
}

/**
 * One print in a project: photo × size × paper × qty, OPTIONALLY framed.
 * When frameId is set, this is a framed print: size_id/paper are constrained to the
 * framed product's printSize/paper, and unit price is the coupled framed price
 * (print INCLUDED). Mirrors D1 project_prints (which has a nullable frame_id column).
 */
export interface PrintSelection {
  sizeId: string;
  paper: PaperId;
  qty: number;
  /** fr-{paperCode}-{printSize} when this print is framed; undefined = loose print. */
  frameId?: string;
  /** Border rendered at bake (default 'none'). Framed prints are always 'none'. */
  borderMode?: BorderMode;
}

/** A grouped cart line: everything with the same size+paper (+frame) collapses to one Shopify line. */
export interface CartGroup {
  /** Shopify SKU — the size's BULK variant SKU (RPLP-CT-…-B) when the bulk tier is active. */
  sku: string;
  sizeId: string;
  paper: PaperId | null;
  family: ProductFamily;
  /** set when this line is a framed product. */
  frameId?: string;
  qty: number;
  /** Unit price of `sku`'s Shopify variant (== bulkPrice when the bulk tier is active). */
  unitPrice: number;
  /** Effective unit price the customer pays (equals unitPrice under the bulk-variant model). */
  effectiveUnitPrice: number;
  bulkApplied: boolean;
  lineTotal: number;
  /** Human-visible line item properties. */
  displayProps: Record<string, string>;
}

export interface Totals {
  groups: CartGroup[];
  subtotal: number;
  /** sizeIds where the bulk tier is active. */
  bulkApplied: string[];
  printCount: number;
}
